import { useEffect, useState } from 'react';
import { DbEventsPage } from '../dbevents/DbEventsPage';
import { ErrorsPage } from '../errors/ErrorsPage';
import { ProfilesPage } from '../profiles/ProfilesPage';
import { type DiagnoseSub, replaceQuery } from '../router';
import { type Range, rangeFromQuery, rangeOf, rangeParams } from '../ui';

/**
 * 진단 메뉴 (22-B회차, 2026-09-08 사용자 확정 5).
 *
 * 오류·DB 감시·CPU 기록 세 화면을 하위 탭으로 묶는다. 셋은 "무엇이 잘못됐나"를 시간대로
 * 파고드는 화면이라 **기간을 함께 쓴다** — 오류에서 어제 오후로 맞춰 놓고 DB 감시로 옮기면
 * 같은 시간대가 열린다. 기간은 여기 하나에 두고 하위 화면은 값과 바꾸는 손만 받는다.
 *
 * 주소에 기간이 실려 오면(`#/diagnose/errors?from&to`) 그것으로 시작한다. 하위 탭을 옮길 때는
 * 주소에 기간이 없어도 이 컴포넌트가 살아 있어 값이 남는다.
 */
export function DiagnosePage({ sub, query }: { sub: DiagnoseSub; query?: string }) {
  const [range, setRange] = useState<Range>(() => rangeFromQuery(query) ?? rangeOf(1));

  // DB 감시·CPU 기록도 기간을 주소에 남긴다 (44회차). 43회차까지는 오류 탭만 써서, 두 탭에서
  // 맞춘 기간이 새로고침·주소 보내기에서 최근 1시간으로 돌아갔다. 오류 탭은 제 조건과 함께 스스로 쓴다
  useEffect(() => {
    if (sub === 'errors') return;
    replaceQuery({ name: 'diagnose', sub, query: rangeParams(range).toString() });
  }, [sub, range]);

  // 같은 하위 탭에서 주소의 기간만 바뀌면(주소를 고치거나 기간 링크로 옴) 화면도 따라간다 (44회차).
  // 자기가 쓴 주소는 지금 기간과 같아 아무 일도 없고, 기간 없는 하위 탭 이동은 지금 기간을 둔다
  useEffect(() => {
    const next = rangeFromQuery(query);
    if (next === undefined) return;
    setRange((prev) => (prev.from === next.from && prev.to === next.to ? prev : next));
  }, [query]);

  if (sub === 'errors') {
    return <ErrorsPage query={query} range={range} onRangeChange={setRange} />;
  }
  if (sub === 'db') return <DbEventsPage range={range} onRangeChange={setRange} />;
  return <ProfilesPage range={range} onRangeChange={setRange} />;
}
